import { apiClient, type ApiResponse } from '@/api';
import { PROPERTY_API_URLS } from '@/api/constants/api.url';
import type {
    BulkImportPropertiesResult,
    CreatePropertyInput,
    ImportPropertyRow,
    LatestPropertyDefaults,
    ListPropertiesParams,
    Property,
    PropertyDetail,
    PropertyDetailResponse,
    PropertyListResponse,
    PropertyPage,
} from '@/modules/properties/types';

/**
 * Single point where the properties module talks to the HTTP API.
 *
 * The list endpoint is offset-paginated (`page` + `limit`) and returns a
 * `total` match count alongside the page. `list()` flattens that to a plain
 * array for callers that just want a set of rows (e.g. picker dropdowns);
 * `listPaged()` keeps the `total` so the table can render server-side paging.
 */
export const propertiesService = {
    /**
     * Fetch properties as a flat array. Used by pickers/dropdowns that want a
     * broad set rather than a single page — defaults to the 200-row API cap.
     */
    async list(params: ListPropertiesParams = {}): Promise<Property[]> {
        const { properties } = await this.listPaged({ limit: 200, ...params });
        return properties;
    },

    /**
     * Location fields of the most recently created property, or null when the
     * workspace has none yet. Seeds the create form so the same ZIP / state /
     * territory / city aren't retyped for every listing.
     */
    async latestDefaults(): Promise<LatestPropertyDefaults | null> {
        const { data } = await apiClient.get<ApiResponse<{ defaults: LatestPropertyDefaults | null }>>(
            PROPERTY_API_URLS.latestDefaults,
        );
        return data.data.defaults;
    },

    /** Fetch one page of properties plus the total match count, for the table. */
    async listPaged(params: ListPropertiesParams = {}): Promise<PropertyPage> {
        const { data } = await apiClient.get<ApiResponse<PropertyListResponse>>(PROPERTY_API_URLS.list, {
            params: {
                page: params.page ?? 1,
                limit: params.limit ?? 25,
                status: params.status,
                q: params.q,
                date_from: params.date_from,
                date_to: params.date_to,
                project_id: params.project_id,
            },
        });
        return { properties: data.data.properties, total: data.data.total };
    },

    async detail(id: string): Promise<PropertyDetail> {
        const { data } = await apiClient.get<ApiResponse<PropertyDetailResponse>>(PROPERTY_API_URLS.detail(id));
        return data.data.property;
    },

    async updateStatus(id: string, status: Property['status']): Promise<void> {
        await apiClient.patch(PROPERTY_API_URLS.update(id), { status });
    },

    async create(body: CreatePropertyInput): Promise<Property> {
        const { data } = await apiClient.post<ApiResponse<{ property: Property }>>(
            PROPERTY_API_URLS.create,
            body,
        );
        return data.data.property;
    },

    /** Import a chunk of properties in one request. The caller splits large files
     *  into chunks (see the import modal); the server inserts each chunk in a
     *  single transaction, deduping on `external_ref`. Returns per-chunk counts,
     *  skipped duplicates, and any rejected rows. */
    async bulkImport(properties: ImportPropertyRow[]): Promise<BulkImportPropertiesResult> {
        const { data } = await apiClient.post<ApiResponse<BulkImportPropertiesResult>>(
            PROPERTY_API_URLS.bulkImport,
            { properties },
        );
        return data.data;
    },

    async update(id: string, body: Partial<CreatePropertyInput>): Promise<Property> {
        const { data } = await apiClient.patch<ApiResponse<{ property: Property }>>(
            PROPERTY_API_URLS.update(id),
            body,
        );
        return data.data.property;
    },

    async remove(id: string): Promise<void> {
        await apiClient.delete(PROPERTY_API_URLS.remove(id));
    },

    /**
     * Tenant-scoped uniqueness check for a Property ID (`external_ref`).
     * Used by the create form for live feedback — the list endpoint caps at
     * 200 rows, so it can't answer this for larger portfolios.
     */
    async checkExternalRef(externalRef: string): Promise<{ available: boolean }> {
        const { data } = await apiClient.get<ApiResponse<{ available: boolean }>>(
            PROPERTY_API_URLS.externalRefAvailability,
            { params: { external_ref: externalRef } },
        );
        return data.data;
    },

    /**
     * Resolve a property by its Property ID (`external_ref`) within the tenant,
     * or `null` if none exists. Powers the importer's dedup: a row whose ID is
     * already present updates that property instead of inserting a duplicate.
     *
     * The list endpoint's free-text `q` searches `external_ref`, so we query by
     * the ID and keep only an exact (case-insensitive) match — `q` also matches
     * title/city/etc., so a substring hit on another column must not count.
     */
    async findByExternalRef(externalRef: string): Promise<Property | null> {
        const ref = externalRef.trim();
        if (!ref) return null;
        const rows = await this.list({ q: ref });
        return rows.find((p) => p.external_ref?.trim().toLowerCase() === ref.toLowerCase()) ?? null;
    },
};
